import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { TrendingUp, TrendingDown, DollarSign, Receipt, Activity, Users, Trophy, BarChart3, PieChart, Calendar as CalendarIcon, Wallet, CreditCard, Zap, Percent, Gamepad2, Coffee, Wine, ShoppingBag, FileText, Building2, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { toast } from 'sonner';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { fr } from 'date-fns/locale';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart, Label, LabelList, Bar, ComposedChart } from 'recharts';

import ObjectifsCaMensuels from '../components/ObjectifsCaMensuels';
import SuiviCaJournalier from '../components/SuiviCaJournalier';
import { useDataChangeListener } from '../utils/dataBus';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

// Composant ObjectifCommercial
const ObjectifCommercial = ({ nom, objectif, salaire, ca, couleur, period }) => {
  const [alertePlayed, setAlertePlayed] = React.useState(false);
  
  const progression = (ca / objectif) * 100;
  const joursEcoules = new Date().getDate();
  const joursDansMois = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate();
  const objectifProrata = (objectif / joursDansMois) * joursEcoules;
  const enRetard = ca < objectifProrata;
  
  const colors = {
    blue: {
      gradient: 'from-blue-500 to-cyan-500',
      bg: 'from-blue-500/10 to-cyan-500/10',
      border: 'border-blue-500/40',
      text: 'text-blue-400'
    },
    purple: {
      gradient: 'from-purple-500 to-pink-500',
      bg: 'from-purple-500/10 to-pink-500/10',
      border: 'border-purple-500/40',
      text: 'text-purple-400'
    }
  };
  
  const style = colors[couleur];
  
  // Alerte si objectif atteint
  React.useEffect(() => {
    if (progression >= 100 && !alertePlayed) {
      setAlertePlayed(true);
      
      // Son de victoire
      const audioSuccess = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGH0fPTgjMGHm7A7+OZFQ0PXK/o76FaFg1Lp+LyvnIdBjGB0PLZizoGJnfD8N+TQwoTYLXp7qJYFQxIoeHyvGsfBjSF0/PZgzQGJXbC8N+UQQ==');
      audioSuccess.volume = 1.0;
      audioSuccess.play().catch(() => {});
      
      toast.success(
        `🎉 OBJECTIF ATTEINT ! ${nom} a réalisé ${progression.toFixed(0)}% de son objectif !`,
        {
          duration: 15000,
          icon: <Trophy className="w-6 h-6 text-yellow-500" />,
          style: {
            background: '#10B981',
            color: '#FFFFFF',
            fontWeight: 'bold',
            fontSize: '16px'
          }
        }
      );
    }
  }, [progression, alertePlayed, nom]);
  
  return (
    <div className="relative group">
      <div className={`absolute inset-0 bg-gradient-to-r ${style.bg} rounded-2xl blur-xl group-hover:blur-2xl transition-all`}></div>
      <div className={`relative bg-gradient-to-br from-[#2A3B5C] to-[#1C2541] rounded-2xl p-6 border ${style.border} hover:border-opacity-100 transition-all hover:scale-105`}>
        
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className={`text-xl font-black text-transparent bg-clip-text bg-gradient-to-r ${style.gradient}`}>
            {nom}
          </h3>
          {progression >= 100 && <Trophy className="w-6 h-6 text-yellow-500 animate-bounce" />}
        </div>
        
        {/* Barre de progression */}
        <div className="mb-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-[#EAEAEA]/60 text-sm">Progression</span>
            <span className={`text-lg font-bold ${progression >= 100 ? 'text-[#10B981]' : progression >= 75 ? 'text-yellow-400' : 'text-red-400'}`}>
              {progression.toFixed(1)}%
            </span>
          </div>
          <div className="h-4 bg-[#0B132B] rounded-full overflow-hidden relative">
            <div 
              className={`h-full bg-gradient-to-r ${style.gradient} transition-all duration-500 relative`}
              style={{ width: `${Math.min(progression, 100)}%` }}
            >
              <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
            </div>
          </div>
        </div>
        
        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <p className="text-[#EAEAEA]/60 text-xs mb-1">CA Actuel</p>
            <p className={`text-xl font-black ${style.text}`}>{ca.toLocaleString()}</p>
            <p className="text-[#EAEAEA]/40 text-xs">FCFA</p>
          </div>
          <div>
            <p className="text-[#EAEAEA]/60 text-xs mb-1">Objectif</p>
            <p className={`text-xl font-black ${style.text}`}>{objectif.toLocaleString()}</p>
            <p className="text-[#EAEAEA]/40 text-xs">FCFA/mois</p>
          </div>
        </div>
        
        {/* Reste à faire */}
        <div className="bg-[#0B132B]/50 rounded-lg p-3">
          <p className="text-[#EAEAEA]/60 text-xs mb-1">Reste à réaliser</p>
          <p className={`text-2xl font-black ${objectif - ca > 0 ? 'text-orange-400' : 'text-[#10B981]'}`}>
            {Math.max(0, objectif - ca).toLocaleString()}
          </p>
          <p className="text-[#EAEAEA]/40 text-xs">FCFA</p>
        </div>
        
        {/* Salaire */}
        <div className="mt-4 pt-4 border-t border-[#C6A15B]/20">
          <div className="flex justify-between items-center">
            <span className="text-[#EAEAEA]/60 text-sm">💰 Salaire</span>
            <span className="text-[#FFD700] font-bold">{salaire.toLocaleString()} FCFA</span>
          </div>
        </div>
        
        {/* Alerte si en retard */}
        {enRetard && period === "Ce Mois" && (
          <div className="mt-3 bg-red-500/20 border border-red-500/40 rounded-lg p-3">
            <p className="text-red-400 text-xs font-semibold">
              ⚠️ En retard sur l'objectif du mois
            </p>
          </div>
        )}
        
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('today');
  const [selectedDate, setSelectedDate] = useState(null);
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [depotsBancaires, setDepotsBancaires] = useState([]);
  const [statsJeux, setStatsJeux] = useState(null);
  const [statsBoissons, setStatsBoissons] = useState(null);
  const [statsClients, setStatsClients] = useState(null);
  const [caMensuel, setCaMensuel] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [objectifsCaData, setObjectifsCaData] = useState(null);
  
  // Nouveau: Mode de sélection (date ou mois)
  const [selectionMode, setSelectionMode] = useState('dates'); // 'dates' ou 'month'
  const [selectedMonth, setSelectedMonth] = useState(null); // {month: 1-12, year: 2025}
  const [selectedMonthYear, setSelectedMonthYear] = useState(new Date().getFullYear());
  
  // Cash Flow state (toujours basé sur le mois en cours)
  const [cashFlowData, setCashFlowData] = useState({ ca: 0, depots: 0, charges: 0, cashFlow: 0 });
  
  // Générer dynamiquement les années disponibles (2025 → année en cours + 2 ans)
  const getAvailableYears = () => {
    const currentYear = new Date().getFullYear();
    const startYear = 2025; // Année de démarrage PlayCity
    const years = [];
    
    // Toujours afficher au minimum jusqu'à currentYear + 2
    const endYear = Math.max(currentYear, 2027); // Minimum 2027 pour consultation future
    
    for (let year = startYear; year <= endYear; year++) {
      years.push(year);
    }
    return years;
  };
  
  const [availableYears] = useState(getAvailableYears());

  useEffect(() => {
    fetchStats();
    fetchDepotsBancaires();
    fetchStatsJeux();
    fetchStatsBoissons();
    fetchStatsClients();
  }, [period, selectedDate, startDate, endDate, selectedMonth]);

  // Refresh instantané sur événement transaction/dépense/dépôt (data bus)
  useDataChangeListener(() => {
    fetchStats();
    fetchDepotsBancaires();
    fetchStatsJeux();
    fetchStatsBoissons();
    fetchStatsClients();
    fetchCaMensuel();
  });

  useEffect(() => {
    fetchCaMensuel();
  }, [selectedYear]); // Fetch CA mensuel quand l'année change
  
  // Fetch objectifs CA quand un mois est sélectionné
  useEffect(() => {
    if (period === 'customMonth' && selectedMonth) {
      fetchObjectifsCa();
    }
  }, [selectedMonth]);

  useEffect(() => {
    fetchObjectifsCa(); // Fetch objectifs CA indépendamment
  }, []); // Une seule fois au chargement

  // useEffect pour Cash Flow - Hybride
  // Mois EN COURS par défaut, SAUF si période personnalisée sélectionnée
  useEffect(() => {
    const fetchCashFlowData = async () => {
      try {
        let startDateToUse, endDateToUse;
        
        // Si période personnalisée, utiliser les dates personnalisées
        if (period === 'custom' && startDate && endDate) {
          startDateToUse = new Date(startDate).toISOString();
          endDateToUse = new Date(endDate).toISOString();
          console.log('🔄 Cash Flow - PÉRIODE PERSONNALISÉE:', startDateToUse, endDateToUse);
        } else {
          // Sinon, toujours utiliser le MOIS EN COURS
          const currentDate = new Date();
          const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
          const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0, 23, 59, 59);
          startDateToUse = startOfMonth.toISOString();
          endDateToUse = endOfMonth.toISOString();
          console.log('🔄 Cash Flow - MOIS EN COURS:', currentDate.getMonth() + 1, currentDate.getFullYear());
        }
        
        const params = {
          start_date: startDateToUse,
          end_date: endDateToUse
        };
        
        // Récupérer CA, dépôts et charges
        const [statsRes, depotsRes] = await Promise.all([
          axios.get(`${API}/dashboard/stats`, { params, withCredentials: true }),
          axios.get(`${API}/depots-bancaires`, { params, withCredentials: true })
        ]);
        
        const caTotal = statsRes.data?.caGeneral || 0;
        const chargesTotal = statsRes.data?.totalDepenses || 0;
        const depotsTotal = depotsRes.data?.reduce((sum, d) => sum + (d.montant || 0), 0) || 0;
        
        const cashFlow = caTotal - depotsTotal - chargesTotal;
        
        console.log('💰 Cash Flow:', {
          ca: caTotal,
          depots: depotsTotal,
          charges: chargesTotal,
          cashFlow: cashFlow
        });
        
        setCashFlowData({
          ca: caTotal,
          depots: depotsTotal,
          charges: chargesTotal,
          cashFlow: cashFlow
        });
      } catch (error) {
        console.error('❌ Erreur Cash Flow:', error);
        setCashFlowData({
          ca: 0,
          depots: 0,
          charges: 0,
          cashFlow: 0
        });
      }
    };
    
    fetchCashFlowData();
  }, [period, startDate, endDate]); // Se recalcule si période custom change

  const fetchStats = async () => {
    setLoading(true);
    try {
      const { startDate, endDate } = getPeriodDates(period);
      const params = { start_date: startDate, end_date: endDate };
      
      const response = await axios.get(`${API}/dashboard/stats`, {
        params,
        withCredentials: true
      });
      setStats(response.data);
    } catch (error) {
      console.error('Erreur stats:', error);
      toast.error('Erreur de chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  const fetchDepotsBancaires = async () => {
    try {
      let startDate, endDate;
      const today = new Date();
      
      if (period === 'today') {
        // Aujourd'hui: dépôts du jour uniquement
        const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0);
        const todayEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);
        startDate = todayStart.toISOString();
        endDate = todayEnd.toISOString();
      } else if (period === 'week') {
        // Semaine: du lundi de la semaine en cours jusqu'à aujourd'hui (ou dimanche si fin de semaine)
        const dayOfWeek = today.getDay(); // 0=Dim, 1=Lun, ..., 6=Sam
        const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        const weekStart = new Date(today.getFullYear(), today.getMonth(), today.getDate() + mondayOffset, 0, 0, 0);
        const weekEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);
        startDate = weekStart.toISOString();
        endDate = weekEnd.toISOString();
      } else if (period === 'month') {
        // Mois: du 1er au dernier jour du mois en cours
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1, 0, 0, 0);
        const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59, 999);
        startDate = startOfMonth.toISOString();
        endDate = endOfMonth.toISOString();
      } else if (period === 'custom' && customStartDate && customEndDate) {
        startDate = new Date(customStartDate).toISOString();
        endDate = new Date(customEndDate).toISOString();
      } else {
        const dates = getPeriodDates(period);
        startDate = dates.startDate;
        endDate = dates.endDate;
      }
      
      const params = { start_date: startDate, end_date: endDate };
      
      const response = await axios.get(`${API}/depots-bancaires`, {
        params,
        withCredentials: true
      });
      setDepotsBancaires(response.data || []);
    } catch (error) {
      console.error('Erreur dépôts bancaires:', error);
    }
  };

  const fetchStatsJeux = async () => {
    try {
      const { startDate, endDate } = getPeriodDates(period);
      const params = { start_date: startDate, end_date: endDate };
      
      const response = await axios.get(`${API}/dashboard/stats-jeux`, {
        params,
        withCredentials: true
      });
      setStatsJeux(response.data || null);
    } catch (error) {
      console.error('Erreur stats jeux:', error);
    }
  };

  const fetchStatsBoissons = async () => {
    try {
      const { startDate, endDate } = getPeriodDates(period);
      const params = { start_date: startDate, end_date: endDate };
      
      const response = await axios.get(`${API}/dashboard/stats-boissons`, {
        params,
        withCredentials: true
      });
      setStatsBoissons(response.data || null);
    } catch (error) {
      console.error('Erreur stats boissons:', error);
    }
  };

  const fetchStatsClients = async () => {
    try {
      const { startDate, endDate } = getPeriodDates(period);
      const params = { start_date: startDate, end_date: endDate };
      
      const response = await axios.get(`${API}/dashboard/stats-clients`, {
        params,
        withCredentials: true
      });
      setStatsClients(response.data || null);
    } catch (error) {
      console.error('Erreur stats clients:', error);
    }
  };

  const fetchCaMensuel = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/ca-mensuel`, {
        params: { annee: selectedYear },
        withCredentials: true
      });
      setCaMensuel(response.data?.data || []);
    } catch (error) {
      console.error('Erreur CA mensuel:', error);
    }
  };

  const fetchObjectifsCa = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/objectifs-ca-mensuels`, {
        withCredentials: true
      });
      setObjectifsCaData(response.data || null);
    } catch (error) {
      console.error('Erreur objectifs CA:', error);
    }
  };

  const getPeriodDates = (periodValue) => {
    // Si mois personnalisé sélectionné
    if (periodValue === 'customMonth' && selectedMonth) {
      const { month, year } = selectedMonth;
      const start = new Date(year, month - 1, 1);
      start.setHours(0, 0, 0, 0);
      const end = new Date(year, month, 0); // Dernier jour du mois
      end.setHours(23, 59, 59, 999);
      return {
        startDate: start.toISOString(),
        endDate: end.toISOString()
      };
    }
    
    // Si période personnalisée (custom range)
    if (periodValue === 'custom' && startDate && endDate) {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      return {
        startDate: start.toISOString(),
        endDate: end.toISOString()
      };
    }

    // Si date unique sélectionnée
    if (periodValue === 'date' && selectedDate) {
      const start = new Date(selectedDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(selectedDate);
      end.setHours(23, 59, 59, 999);
      return {
        startDate: start.toISOString(),
        endDate: end.toISOString()
      };
    }

    const now = new Date();
    let startDateVal, endDateVal;

    switch (periodValue) {
      case 'today':
        startDateVal = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        endDateVal = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        break;
      case 'week':
        const dayOfWeek = now.getDay();
        const diff = now.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
        startDateVal = new Date(now.setDate(diff));
        startDateVal.setHours(0, 0, 0, 0);
        endDateVal = new Date();
        break;
      case 'month':
        startDateVal = new Date(now.getFullYear(), now.getMonth(), 1);
        endDateVal = new Date();
        break;
      default:
        startDateVal = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        endDateVal = new Date();
    }

    return {
      startDate: startDateVal.toISOString(),
      endDate: endDateVal.toISOString()
    };
  };

  const handlePeriodChange = (value) => {
    setPeriod(value);
    if (value === 'custom') {
      setShowDatePicker(true);
    } else {
      setDateRange([null, null]);
    }
  };

  const handleDateRangeChange = (update) => {
    setDateRange(update);
    
    // Si les deux dates sont sélectionnées (début et fin), fermer automatiquement
    if (update[0] && update[1]) {
      setPeriod('custom');
      setSelectedMonth(null); // Reset month selection
      // Fermeture automatique avec délai pour voir la sélection validée
      setTimeout(() => {
        setShowDatePicker(false);
      }, 800);
    }
  };

  const handleMonthSelection = (month, year) => {
    setSelectedMonth({ month, year });
    setPeriod('customMonth');
    // Fermeture automatique après sélection du mois
    setTimeout(() => {
      setShowDatePicker(false);
    }, 600);
  };

  const handleCustomRangeConfirm = () => {
    if (startDate && endDate) {
      setPeriod('custom');
      setShowDatePicker(false);
    } else if (startDate && !endDate) {
      // Si seulement date de début sélectionnée, utiliser la même date pour fin
      setDateRange([startDate, startDate]);
      setPeriod('custom');
      setShowDatePicker(false);
    } else {
      toast.error('Veuillez sélectionner au moins une date');
    }
  };

  const getPeriodLabel = () => {
    // Mode mois sélectionné
    if (period === 'customMonth' && selectedMonth) {
      const monthNames = ['Jan', 'Fév', 'Mars', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sept', 'Oct', 'Nov', 'Déc'];
      return `${monthNames[selectedMonth.month - 1]} ${selectedMonth.year}`;
    }
    
    // Mode dates sélectionnées
    if (period === 'custom' && startDate && endDate) {
      const start = new Date(startDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
      const end = new Date(endDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
      
      // Si même jour, afficher juste une date
      if (startDate.toDateString() === endDate.toDateString()) {
        return new Date(startDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
      }
      return `${start} - ${end}`;
    }
    
    // Sinon toujours afficher "Période" (pas de label pour today/week/month ici)
    return "Période";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="relative">
          <div className="w-20 h-20 border-4 border-[#C6A15B]/30 rounded-full"></div>
          <div className="w-20 h-20 border-4 border-[#C6A15B] border-t-transparent rounded-full animate-spin absolute top-0"></div>
        </div>
      </div>
    );
  }

  const displayStats = stats || {};
  const marge = displayStats.caGeneral > 0 ? ((displayStats.benefice / displayStats.caGeneral) * 100).toFixed(2) : 0;
  
  // Calculer le total des dépôts à partir de depotsBancaires (au lieu de displayStats.totalDepots)
  const totalDepots = depotsBancaires.reduce((sum, depot) => sum + (depot.montant || 0), 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0B132B] via-[#1C2541] to-[#0B132B] p-3 sm:p-4 lg:p-8 pb-24 xl:pb-8">
      {/* Header ULTRA PREMIUM */}
      <div className="max-w-7xl mx-auto mb-6 lg:mb-8">
        {/* Titre */}
        <div className="mb-6">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#FFD700] via-[#C6A15B] to-[#FFD700]">
            Dashboard
          </h1>
          <p className="text-[#C6A15B]/80 mt-2 text-sm lg:text-lg">Vue d'ensemble de votre activité</p>
        </div>

        {/* Filtres période - SUR UNE SEULE LIGNE - ULTRA PREMIUM */}
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-[#C6A15B]/10 to-[#FFD700]/10 rounded-2xl blur-xl"></div>
          <div className="relative bg-gradient-to-br from-[#1C2541]/80 to-[#2A3B5C]/80 backdrop-blur-xl rounded-2xl p-4 lg:p-6 border-2 border-[#C6A15B]/30 shadow-2xl">
            <div className="flex flex-wrap items-center justify-center gap-2 lg:gap-3">
              <Button
                onClick={() => handlePeriodChange('today')}
                className={`relative px-4 sm:px-6 lg:px-8 py-2.5 lg:py-3 rounded-xl font-bold text-sm lg:text-base transition-all duration-300 ${
                  period === 'today'
                    ? 'bg-gradient-to-r from-[#C6A15B] to-[#B89149] text-[#0B132B] shadow-lg shadow-[#C6A15B]/50 scale-105'
                    : 'bg-[#1C2541] text-[#EAEAEA] hover:bg-[#2A3B5C] border border-[#C6A15B]/30 hover:scale-105'
                }`}
              >
                <Zap className="w-4 h-4 lg:w-5 lg:h-5 mr-2 inline" />
                Aujourd'hui
              </Button>

              <Button
                onClick={() => handlePeriodChange('week')}
                className={`relative px-4 sm:px-6 lg:px-8 py-2.5 lg:py-3 rounded-xl font-bold text-sm lg:text-base transition-all duration-300 ${
                  period === 'week'
                    ? 'bg-gradient-to-r from-[#C6A15B] to-[#B89149] text-[#0B132B] shadow-lg shadow-[#C6A15B]/50 scale-105'
                    : 'bg-[#1C2541] text-[#EAEAEA] hover:bg-[#2A3B5C] border border-[#C6A15B]/30 hover:scale-105'
                }`}
              >
                📅 Semaine
              </Button>

              <Button
                onClick={() => handlePeriodChange('month')}
                className={`relative px-4 sm:px-6 lg:px-8 py-2.5 lg:py-3 rounded-xl font-bold text-sm lg:text-base transition-all duration-300 ${
                  period === 'month'
                    ? 'bg-gradient-to-r from-[#C6A15B] to-[#B89149] text-[#0B132B] shadow-lg shadow-[#C6A15B]/50 scale-105'
                    : 'bg-[#1C2541] text-[#EAEAEA] hover:bg-[#2A3B5C] border border-[#C6A15B]/30 hover:scale-105'
                }`}
              >
                📊 Mois
              </Button>

              <Button
                onClick={() => handlePeriodChange('custom')}
                className={`relative px-4 sm:px-6 lg:px-8 py-2.5 lg:py-3 rounded-xl font-bold text-sm lg:text-base transition-all duration-300 ${
                  (period === 'custom' || period === 'customMonth')
                    ? 'bg-gradient-to-r from-[#C6A15B] to-[#B89149] text-[#0B132B] shadow-lg shadow-[#C6A15B]/50 scale-105'
                    : 'bg-[#1C2541] text-[#EAEAEA] hover:bg-[#2A3B5C] border border-[#C6A15B]/30 hover:scale-105'
                }`}
              >
                <CalendarIcon className="w-4 h-4 lg:w-5 lg:h-5 mr-2 inline" />
                {getPeriodLabel()}
              </Button>
            </div>
          </div>
        </div>

        {/* Calendrier ULTRA PREMIUM WAHOO - Version Unifiée avec Onglets */}
        {showDatePicker && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-50 flex items-center justify-center p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowDatePicker(false);
                setDateRange([null, null]);
              }
            }}
          >
            {/* ULTRA PREMIUM Container */}
            <div className="relative max-w-md w-full">
              {/* Mega Glow Effect */}
              <div className="absolute -inset-4 bg-gradient-to-r from-[#FFD700] via-[#C6A15B] to-[#FFD700] rounded-3xl blur-3xl opacity-40 animate-pulse"></div>
              
              <div className="relative bg-gradient-to-br from-[#1C2541] via-[#2A3B5C] to-[#1C2541] rounded-3xl p-6 border-4 border-[#C6A15B]/60 shadow-2xl"
                style={{
                  boxShadow: '0 25px 80px -20px rgba(198, 161, 91, 0.6), inset 0 -2px 20px rgba(255, 215, 0, 0.1)'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header ULTRA PREMIUM */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <div className="absolute inset-0 bg-gradient-to-br from-[#FFD700] to-[#C6A15B] rounded-xl blur-lg opacity-75"></div>
                      <div className="relative w-12 h-12 bg-gradient-to-br from-[#FFD700] to-[#C6A15B] rounded-xl flex items-center justify-center shadow-xl">
                        <CalendarIcon className="w-7 h-7 text-[#0B132B]" strokeWidth={2.5} />
                      </div>
                    </div>
                    <h3 className="text-xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#FFD700] to-[#C6A15B]"
                      style={{ textShadow: '0 0 20px rgba(255, 215, 0, 0.5)' }}
                    >
                      Période
                    </h3>
                  </div>
                  <button 
                    onClick={() => {
                      setShowDatePicker(false);
                      setDateRange([null, null]);
                    }}
                    className="w-10 h-10 rounded-full bg-gradient-to-br from-red-500 to-red-600 text-white flex items-center justify-center text-2xl font-bold hover:scale-110 transition-all shadow-lg hover:shadow-red-500/50"
                  >
                    ×
                  </button>
                </div>

                {/* ONGLETS: Dates / Mois */}
                <div className="flex gap-2 mb-4">
                  <button
                    onClick={() => setSelectionMode('dates')}
                    className={`flex-1 py-3 px-4 rounded-xl font-bold text-sm transition-all duration-300 ${
                      selectionMode === 'dates'
                        ? 'bg-gradient-to-r from-[#FFD700] to-[#C6A15B] text-[#0B132B] shadow-lg shadow-[#FFD700]/50'
                        : 'bg-[#0B132B]/50 text-[#EAEAEA]/60 hover:text-[#EAEAEA] hover:bg-[#0B132B]/70'
                    }`}
                  >
                    📅 Dates
                  </button>
                  <button
                    onClick={() => setSelectionMode('month')}
                    className={`flex-1 py-3 px-4 rounded-xl font-bold text-sm transition-all duration-300 ${
                      selectionMode === 'month'
                        ? 'bg-gradient-to-r from-[#FFD700] to-[#C6A15B] text-[#0B132B] shadow-lg shadow-[#FFD700]/50'
                        : 'bg-[#0B132B]/50 text-[#EAEAEA]/60 hover:text-[#EAEAEA] hover:bg-[#0B132B]/70'
                    }`}
                  >
                    🗓 Mois
                  </button>
                </div>

                {/* MODE DATES - DatePicker */}
                {selectionMode === 'dates' && (
                  <div className="premium-calendar-wrapper ultra-premium">
                    <DatePicker
                      selectsRange={true}
                      startDate={startDate}
                      endDate={endDate}
                      onChange={handleDateRangeChange}
                      inline
                      locale={fr}
                      dateFormat="dd/MM/yyyy"
                      monthsShown={1}
                      calendarClassName="premium-calendar"
                    />
                  </div>
                )}

                {/* MODE MOIS - Sélecteur Mois/Année */}
                {selectionMode === 'month' && (
                  <div className="space-y-4">
                    {/* Sélecteur d'année */}
                    <div className="flex items-center justify-center gap-3 bg-gradient-to-r from-[#FFD700]/20 to-[#C6A15B]/20 rounded-xl p-3 border-2 border-[#C6A15B]/40">
                      <button
                        onClick={() => setSelectedMonthYear(prev => prev - 1)}
                        className="w-10 h-10 rounded-lg bg-[#0B132B] text-[#FFD700] font-bold hover:bg-[#C6A15B] hover:text-[#0B132B] transition-all"
                      >
                        ◀
                      </button>
                      <span className="text-2xl font-black text-[#FFD700] min-w-[100px] text-center">
                        {selectedMonthYear}
                      </span>
                      <button
                        onClick={() => setSelectedMonthYear(prev => prev + 1)}
                        className="w-10 h-10 rounded-lg bg-[#0B132B] text-[#FFD700] font-bold hover:bg-[#C6A15B] hover:text-[#0B132B] transition-all"
                      >
                        ▶
                      </button>
                    </div>

                    {/* Grille de mois */}
                    <div className="grid grid-cols-3 gap-2">
                      {['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'].map((monthName, index) => {
                        const isSelected = selectedMonth?.month === (index + 1) && selectedMonth?.year === selectedMonthYear;
                        const currentMonth = new Date().getMonth();
                        const currentYear = new Date().getFullYear();
                        const isCurrentMonth = (index === currentMonth) && (selectedMonthYear === currentYear);
                        
                        return (
                          <button
                            key={monthName}
                            onClick={() => handleMonthSelection(index + 1, selectedMonthYear)}
                            className={`
                              relative py-3 px-2 rounded-xl font-bold text-sm transition-all duration-300
                              ${isSelected
                                ? 'bg-gradient-to-br from-[#FFD700] to-[#C6A15B] text-[#0B132B] shadow-lg scale-105'
                                : 'bg-[#1C2541] text-[#EAEAEA] hover:bg-[#2A3B5C] hover:scale-105'
                              }
                              ${isCurrentMonth && !isSelected ? 'ring-2 ring-[#FFD700]' : ''}
                            `}
                          >
                            {monthName}
                            {isCurrentMonth && !isSelected && (
                              <div className="absolute top-1 right-1 w-2 h-2 bg-[#FFD700] rounded-full animate-pulse"></div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Aperçu Premium - Dates sélectionnées */}
                {selectionMode === 'dates' && startDate && endDate && (
                  <div className="relative mt-4 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-[#10B981]/20 via-[#34D399]/20 to-[#10B981]/20 rounded-2xl blur-xl animate-pulse"></div>
                    <div className="relative bg-gradient-to-r from-[#10B981]/30 to-[#059669]/30 rounded-2xl p-4 border-2 border-[#10B981]/50">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-2 h-2 bg-[#10B981] rounded-full animate-ping"></div>
                        <p className="text-[#10B981] text-sm font-black uppercase tracking-wider">✨ Dates sélectionnées</p>
                      </div>
                      {startDate.toDateString() === endDate.toDateString() ? (
                        <p className="text-white font-bold text-lg">
                          📅 {new Date(startDate).toLocaleDateString('fr-FR', { 
                            day: 'numeric', 
                            month: 'long', 
                            year: 'numeric' 
                          })}
                        </p>
                      ) : (
                        <div>
                          <p className="text-white font-bold text-lg">
                            {new Date(startDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                            <span className="text-[#10B981] mx-2">→</span>
                            {new Date(endDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </p>
                          <p className="text-white/80 text-sm mt-1">
                            📊 {Math.ceil((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24)) + 1} jour(s)
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Aperçu Premium - Mois sélectionné */}
                {selectionMode === 'month' && selectedMonth && (
                  <div className="relative mt-4 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-[#FFD700]/20 via-[#C6A15B]/20 to-[#FFD700]/20 rounded-2xl blur-xl animate-pulse"></div>
                    <div className="relative bg-gradient-to-r from-[#FFD700]/30 to-[#C6A15B]/30 rounded-2xl p-4 border-2 border-[#FFD700]/50">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-2 h-2 bg-[#FFD700] rounded-full animate-ping"></div>
                        <p className="text-[#FFD700] text-sm font-black uppercase tracking-wider">🗓 Mois sélectionné</p>
                      </div>
                      <p className="text-white font-bold text-lg">
                        {new Date(selectedMonth.year, selectedMonth.month - 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* SECTION 1 - TOTAL GÉNÉRAL - ULTRA PREMIUM */}
        <div className="relative mb-12">
          {/* Effet de lueur en arrière-plan */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#C6A15B]/10 via-[#FFD700]/10 to-[#C6A15B]/10 rounded-3xl blur-2xl"></div>
          
          {/* Cercle Session en Cours - RESPONSIVE PLACEMENT */}
          <div className="absolute top-2 right-2 sm:top-8 sm:right-8 z-30">
            <div className="flex flex-col items-center gap-1 sm:gap-2">
              {/* Cercle avec animations - TAILLE ADAPTATIVE */}
              <div className="relative group">
                {/* Glow effet animé */}
                <div className="absolute -inset-2 bg-gradient-to-r from-blue-500/30 via-cyan-500/30 to-blue-500/30 rounded-full blur-lg animate-pulse"></div>
                
                {/* Cercle principal 3D - RESPONSIVE */}
                <div className="relative w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gradient-to-br from-[#1C2541] via-[#2A3B5C] to-[#0B132B] shadow-[0_10px_20px_rgba(0,0,0,0.5),inset_0_1px_3px_rgba(255,255,255,0.2)] border-2 border-[#3A4A6C]/50 flex items-center justify-center group-hover:scale-110 transition-all duration-300">
                  {/* Shine animé */}
                  <div className="absolute inset-0 rounded-full overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-white/20 via-transparent to-transparent opacity-60"></div>
                  </div>
                  
                  {/* Ring extérieur animé */}
                  <div className="absolute -inset-1 rounded-full border-2 border-cyan-400/20 animate-spin" style={{animationDuration: '8s'}}></div>
                  
                  {/* Nombre DORÉ LUXE - RESPONSIVE */}
                  <p className="relative z-10 text-2xl sm:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-br from-amber-300 via-yellow-200 to-amber-300 drop-shadow-[0_2px_8px_rgba(252,211,77,0.8)]">
                    {displayStats.activeSessions || '0'}
                  </p>
                </div>
              </div>
              
              {/* Texte en dessous - RESPONSIVE */}
              <div className="relative hidden sm:block">
                <div className="absolute inset-0 bg-cyan-400/10 blur-sm rounded-lg"></div>
                <p className="relative text-[10px] font-bold text-cyan-200 uppercase tracking-wider px-3 py-1 whitespace-nowrap">
                  Session en cours
                </p>
              </div>
            </div>
          </div>
          
          <div className="relative bg-gradient-to-br from-[#1C2541]/90 to-[#2A3B5C]/90 backdrop-blur-xl rounded-3xl p-4 sm:p-6 lg:p-8 border-2 border-[#C6A15B]/30 shadow-2xl">
            {/* Titre de la section - RESPONSIVE avec margin-right pour le cercle */}
            <div className="mb-8 text-center pr-16 sm:pr-0">
              <div className="inline-block relative">
                <div className="absolute inset-0 bg-gradient-to-r from-[#FFD700] to-[#C6A15B] blur-lg opacity-50"></div>
                <h2 className="relative text-xl sm:text-2xl md:text-3xl lg:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#FFD700] via-[#C6A15B] to-[#FFD700] px-2 sm:px-4 md:px-6 py-2">
                  TOTAL GÉNÉRAL
                </h2>
              </div>
              <p className="text-[#EAEAEA]/60 mt-2 text-xs sm:text-sm uppercase tracking-widest">
                {getPeriodLabel()}
              </p>
            </div>

            {/* Grille des 6 KPI Ultra Premium */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              
              {/* KPI 1 - CA Total */}
              <div className="group relative">
                <div className="absolute inset-0 bg-gradient-to-r from-[#FFD700]/20 to-[#C6A15B]/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-300"></div>
                <div className="relative bg-gradient-to-br from-[#2A3B5C] to-[#1C2541] rounded-2xl p-6 border border-[#FFD700]/40 hover:border-[#FFD700] transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-[#FFD700]/30">
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 bg-gradient-to-br from-[#FFD700] to-[#C6A15B] rounded-xl">
                      <DollarSign className="w-6 h-6 text-[#0B132B]" strokeWidth={3} />
                    </div>
                    <TrendingUp className="w-5 h-5 text-green-400" />
                  </div>
                  <h3 className="text-[#EAEAEA]/60 text-sm font-semibold mb-2 uppercase tracking-wider">CA Total</h3>
                  <div className="flex items-baseline gap-2">
                    <p className="text-3xl md:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#FFD700] to-[#C6A15B]">
                      {displayStats.caGeneral?.toLocaleString() || '0'}
                    </p>
                    <span className="text-[#FFD700] font-bold text-lg">FCFA</span>
                  </div>
                  <p className="text-[#EAEAEA]/40 text-xs mt-2">Globale</p>
                </div>
              </div>

              {/* KPI 2 - Charges (Dépenses) */}
              <div className="group relative">
                <div className="absolute inset-0 bg-gradient-to-r from-red-500/20 to-orange-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-300"></div>
                <div className="relative bg-gradient-to-br from-[#2A3B5C] to-[#1C2541] rounded-2xl p-6 border border-red-500/40 hover:border-red-500 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-red-500/30">
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 bg-gradient-to-br from-red-500 to-orange-500 rounded-xl">
                      <Receipt className="w-6 h-6 text-white" strokeWidth={3} />
                    </div>
                    <TrendingDown className="w-5 h-5 text-red-400" />
                  </div>
                  <h3 className="text-[#EAEAEA]/60 text-sm font-semibold mb-2 uppercase tracking-wider">Charges</h3>
                  <div className="flex items-baseline gap-2">
                    <p className="text-3xl md:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-500">
                      {displayStats.totalDepenses?.toLocaleString() || '0'}
                    </p>
                    <span className="text-red-400 font-bold text-lg">FCFA</span>
                  </div>
                  <p className="text-[#EAEAEA]/40 text-xs mt-2">Globale</p>
                </div>
              </div>

              {/* KPI 3 - Bénéfice */}
              <div className="group relative">
                <div className="absolute inset-0 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-300"></div>
                <div className="relative bg-gradient-to-br from-[#2A3B5C] to-[#1C2541] rounded-2xl p-6 border border-green-500/40 hover:border-green-500 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-green-500/30">
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl">
                      <TrendingUp className="w-6 h-6 text-white" strokeWidth={3} />
                    </div>
                    <BarChart3 className="w-5 h-5 text-green-400" />
                  </div>
                  <h3 className="text-[#EAEAEA]/60 text-sm font-semibold mb-2 uppercase tracking-wider">Bénéfice</h3>
                  <div className="flex items-baseline gap-2">
                    <p className={`text-3xl md:text-4xl font-black text-transparent bg-clip-text ${
                      displayStats.benefice >= 0 
                        ? 'bg-gradient-to-r from-green-500 to-emerald-500'
                        : 'bg-gradient-to-r from-red-500 to-orange-500'
                    }`}>
                      {displayStats.benefice?.toLocaleString() || '0'}
                    </p>
                    <span className={`font-bold text-lg ${displayStats.benefice >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      FCFA
                    </span>
                  </div>
                  <p className="text-[#EAEAEA]/40 text-xs mt-2">CA - Charges</p>
                </div>
              </div>

              {/* KPI 4 - Dépôts Bancaire */}
              <div className="group relative">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-300"></div>
                <div className="relative bg-gradient-to-br from-[#2A3B5C] to-[#1C2541] rounded-2xl p-6 border border-purple-500/40 hover:border-purple-500 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-purple-500/30">
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl">
                      <Wallet className="w-6 h-6 text-white" strokeWidth={3} />
                    </div>
                    <CreditCard className="w-5 h-5 text-purple-400" />
                  </div>
                  <h3 className="text-[#EAEAEA]/60 text-sm font-semibold mb-2 uppercase tracking-wider">Dépôts Bancaire</h3>
                  <div className="flex items-baseline gap-2">
                    <p className="text-3xl md:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-500 to-pink-500">
                      {totalDepots?.toLocaleString() || '0'}
                    </p>
                    <span className="text-purple-400 font-bold text-lg">FCFA</span>
                  </div>
                  <p className="text-[#EAEAEA]/40 text-xs mt-2">
                    {period === 'today' ? "Aujourd'hui" : period === 'week' ? 'Semaine en cours' : period === 'month' ? 'Mois en cours' : 'Période'}
                  </p>
                </div>
              </div>

              {/* KPI 5 - Cash Flow */}
              <div className="group relative">
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 to-teal-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-300"></div>
                <div className="relative bg-gradient-to-br from-[#2A3B5C] to-[#1C2541] rounded-2xl p-6 border border-cyan-500/40 hover:border-cyan-500 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-cyan-500/30">
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-xl">
                      <Zap className="w-6 h-6 text-white" strokeWidth={3} />
                    </div>
                    {cashFlowData.cashFlow >= 0 ? (
                      <ArrowUpRight className="w-5 h-5 text-cyan-400" />
                    ) : (
                      <ArrowDownRight className="w-5 h-5 text-red-400" />
                    )}
                  </div>
                  <h3 className="text-[#EAEAEA]/60 text-sm font-semibold mb-2 uppercase tracking-wider">Cash Flow</h3>
                  <div className="flex items-baseline gap-2">
                    <p className={`text-3xl md:text-4xl font-black text-transparent bg-clip-text ${
                      cashFlowData.cashFlow >= 0 
                        ? 'bg-gradient-to-r from-cyan-500 to-teal-500'
                        : 'bg-gradient-to-r from-red-500 to-orange-500'
                    }`}>
                      {cashFlowData.cashFlow?.toLocaleString() || '0'}
                    </p>
                    <span className={`font-bold text-lg ${cashFlowData.cashFlow >= 0 ? 'text-cyan-400' : 'text-red-400'}`}>
                      FCFA
                    </span>
                  </div>
                  <p className="text-[#EAEAEA]/40 text-xs mt-2">Mois en cours</p>
                </div>
              </div>

              {/* KPI 6 - Marge */}
              <div className="group relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-300"></div>
                <div className="relative bg-gradient-to-br from-[#2A3B5C] to-[#1C2541] rounded-2xl p-6 border border-blue-500/40 hover:border-blue-500 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-blue-500/30">
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl">
                      <Percent className="w-6 h-6 text-white" strokeWidth={3} />
                    </div>
                    <BarChart3 className="w-5 h-5 text-blue-400" />
                  </div>
                  <h3 className="text-[#EAEAEA]/60 text-sm font-semibold mb-2 uppercase tracking-wider">Marge</h3>
                  <div className="flex items-baseline gap-2">
                    <p className="text-3xl md:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-cyan-500">
                      {marge}
                    </p>
                    <span className="text-blue-400 font-bold text-lg">%</span>
                  </div>
                  <p className="text-[#EAEAEA]/40 text-xs mt-2">Globale</p>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* SECTION 1.4 - SUIVI CA JOURNALIER (CEO Session 7)
            Avant Objectifs CA Mensuels. Admin+Cashier voient tous, Staff voit son propre suivi. */}
        <SuiviCaJournalier />

        {/* SECTION 1.5 - OBJECTIFS CA COMMERCIAUX - ULTRA PREMIUM */}
        <ObjectifsCaMensuels 
          dashboardPeriod={period}
          dashboardSelectedMonth={selectedMonth}
        />

        {/* SECTION 2 - PERFORMANCE DES COMMERCIAUX - ULTRA PREMIUM
            CEO Session 5/6 : visible pour admin ET cashier (Manager des commerciaux).
            Staff (Playmaster) ne voit PAS le podium. */}
        {(user?.role === 'admin' || user?.role === 'cashier') && displayStats.statsParCommercial && displayStats.statsParCommercial.length > 0 && (
          <div className="relative mb-12">
            {/* Effet de lueur en arrière-plan */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#3B82F6]/10 via-[#8B5CF6]/10 to-[#3B82F6]/10 rounded-3xl blur-2xl"></div>
            
            <div className="relative bg-gradient-to-br from-[#1C2541]/90 to-[#2A3B5C]/90 backdrop-blur-xl rounded-3xl p-8 border-2 border-[#3B82F6]/30 shadow-2xl">
              {/* Titre de la section */}
              <div className="mb-8 text-center">
                <div className="inline-block relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] blur-lg opacity-50"></div>
                  <h2 className="relative text-3xl md:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#3B82F6] via-[#8B5CF6] to-[#3B82F6] px-6 py-2">
                    PERFORMANCE DES COMMERCIAUX
                  </h2>
                </div>
                <p className="text-[#EAEAEA]/60 mt-2 text-sm uppercase tracking-widest">
                  {getPeriodLabel()}
                </p>
              </div>

              {/* Layout : Podium à gauche + Tableau à droite */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                
                {/* GAUCHE - TOP 3 COMMERCIAUX PODIUM */}
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#FFD700]/10 to-[#C6A15B]/10 rounded-2xl blur-xl"></div>
                  <div className="relative bg-gradient-to-br from-[#2A3B5C] to-[#1C2541] rounded-2xl p-6 border border-[#FFD700]/40">
                    
                    {/* Titre avec icône trophée */}
                    <div className="flex items-center gap-3 mb-6">
                      <div className="p-3 bg-gradient-to-br from-[#FFD700] to-[#C6A15B] rounded-xl">
                        <Trophy className="w-6 h-6 text-[#0B132B]" strokeWidth={3} />
                      </div>
                      <h3 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#FFD700] to-[#C6A15B]">
                        Top 3 Commerciaux
                      </h3>
                    </div>

                    {/* Podium */}
                    <div className="space-y-4">
                      {displayStats.statsParCommercial.slice(0, 3).map((commercial, index) => {
                        const colors = [
                          { bg: 'from-yellow-500 to-amber-500', text: 'text-yellow-400', border: 'border-yellow-500/40', shadow: 'shadow-yellow-500/30', medal: '🥇' },
                          { bg: 'from-gray-400 to-gray-500', text: 'text-gray-400', border: 'border-gray-500/40', shadow: 'shadow-gray-500/30', medal: '🥈' },
                          { bg: 'from-orange-600 to-orange-700', text: 'text-orange-400', border: 'border-orange-600/40', shadow: 'shadow-orange-600/30', medal: '🥉' }
                        ];
                        const style = colors[index];

                        return (
                          <div key={commercial.commercialId} className={`group relative hover:scale-105 transition-all duration-300`}>
                            <div className={`absolute inset-0 bg-gradient-to-r ${style.bg} opacity-20 rounded-xl blur-xl group-hover:blur-2xl transition-all`}></div>
                            <div className={`relative bg-gradient-to-br from-[#2A3B5C] to-[#1C2541] rounded-xl p-4 sm:p-5 border ${style.border} hover:border-opacity-100 transition-all hover:shadow-2xl ${style.shadow}`}>
                              <div className="flex items-center gap-3 sm:gap-4">
                                {/* Médaille avec numéro */}
                                <div className={`flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gradient-to-br ${style.bg} flex items-center justify-center text-xl sm:text-2xl font-black shadow-lg`}>
                                  {index + 1}
                                </div>
                                
                                {/* Info Commercial */}
                                <div className="flex-1 min-w-0">
                                  <h4 className={`text-base sm:text-lg font-bold ${style.text} break-words`}>
                                    {commercial.commercialName}
                                  </h4>
                                  <p className="text-[#EAEAEA]/60 text-xs sm:text-sm">
                                    {commercial.nbTransactions} ventes
                                  </p>
                                </div>

                                {/* CA */}
                                <div className="text-right flex-shrink-0">
                                  <p className={`text-lg sm:text-2xl font-black ${style.text}`}>
                                    {commercial.ca.toLocaleString()}
                                  </p>
                                  <p className="text-[#EAEAEA]/40 text-xs">FCFA</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* DROITE - TABLEAU KPI PAR COMMERCIAL */}
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#3B82F6]/10 to-[#8B5CF6]/10 rounded-2xl blur-xl"></div>
                  <div className="relative bg-gradient-to-br from-[#2A3B5C] to-[#1C2541] rounded-2xl p-6 border border-[#3B82F6]/40">
                    
                    {/* Titre du tableau */}
                    <div className="flex items-center gap-3 mb-6">
                      <div className="p-3 bg-gradient-to-br from-[#3B82F6] to-[#8B5CF6] rounded-xl">
                        <BarChart3 className="w-6 h-6 text-white" strokeWidth={3} />
                      </div>
                      <h3 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6]">
                        Worker KPI
                      </h3>
                    </div>

                    {/* Tableau moderne */}
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-full">
                        {/* En-têtes */}
                        <thead>
                          <tr className="bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] text-white">
                            <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm font-bold uppercase tracking-wider rounded-tl-lg">
                              Métrique
                            </th>
                            {displayStats.statsParCommercial.map((commercial, index) => (
                              <th key={commercial.commercialId} className={`px-2 sm:px-4 py-2 sm:py-3 text-center text-xs sm:text-sm font-bold uppercase tracking-wider ${
                                index === displayStats.statsParCommercial.length - 1 ? 'rounded-tr-lg' : ''
                              }`}>
                                <div className="break-words min-w-[60px] sm:min-w-0">
                                  {commercial.commercialName.split(' ')[0]}
                                </div>
                              </th>
                            ))}
                          </tr>
                        </thead>

                        {/* Corps du tableau */}
                        <tbody className="divide-y divide-[#3B82F6]/20">
                          {/* Ligne 1 - Nbre de Clients */}
                          <tr className="hover:bg-[#3B82F6]/10 transition-colors">
                            <td className="px-2 sm:px-4 py-2 sm:py-4 text-[#EAEAEA] font-semibold text-xs sm:text-base">
                              Nbre de Clients
                            </td>
                            {displayStats.statsParCommercial.map(commercial => (
                              <td key={commercial.commercialId} className="px-2 sm:px-4 py-2 sm:py-4 text-center">
                                <span className="text-[#3B82F6] font-bold text-sm sm:text-lg">
                                  {commercial.nbClients || 0}
                                </span>
                              </td>
                            ))}
                          </tr>

                          {/* Ligne 2 - Nbre de Sessions */}
                          <tr className="hover:bg-[#8B5CF6]/10 transition-colors">
                            <td className="px-2 sm:px-4 py-2 sm:py-4 text-[#EAEAEA] font-semibold text-xs sm:text-base">
                              Nbre de Sessions
                            </td>
                            {displayStats.statsParCommercial.map(commercial => (
                              <td key={commercial.commercialId} className="px-2 sm:px-4 py-2 sm:py-4 text-center">
                                <span className="text-[#8B5CF6] font-bold text-sm sm:text-lg">
                                  {commercial.nbSessions || 0}
                                </span>
                              </td>
                            ))}
                          </tr>

                          {/* Ligne 3 - Chiffre d'affaires */}
                          <tr className="hover:bg-[#10B981]/10 transition-colors">
                            <td className="px-2 sm:px-4 py-2 sm:py-4 text-[#EAEAEA] font-semibold text-xs sm:text-base">
                              Chiffre d'affaires
                            </td>
                            {displayStats.statsParCommercial.map(commercial => (
                              <td key={commercial.commercialId} className="px-2 sm:px-4 py-2 sm:py-4 text-center">
                                <div>
                                  <span className="text-[#10B981] font-black text-sm sm:text-lg">
                                    {commercial.ca.toLocaleString()}
                                  </span>
                                  <p className="text-[#EAEAEA]/40 text-xs">FCFA</p>
                                </div>
                              </td>
                            ))}
                          </tr>
                        </tbody>
                      </table>
                    </div>

                  </div>
                </div>

              </div>
            </div>
          </div>
        )}

        {/* SECTION 3 - KPI PAR CATÉGORIES - ULTRA PREMIUM */}
        <div className="relative mb-12">
          {/* Effet de lueur en arrière-plan */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#10B981]/10 via-[#3B82F6]/10 to-[#F59E0B]/10 rounded-3xl blur-2xl"></div>
          
          <div className="relative bg-gradient-to-br from-[#1C2541]/90 to-[#2A3B5C]/90 backdrop-blur-xl rounded-3xl p-8 border-2 border-[#10B981]/30 shadow-2xl">
            {/* Titre de la section */}
            <div className="mb-8 text-center">
              <div className="inline-block relative">
                <div className="absolute inset-0 bg-gradient-to-r from-[#10B981] to-[#3B82F6] blur-lg opacity-50"></div>
                <h2 className="relative text-3xl md:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] via-[#3B82F6] to-[#F59E0B] px-6 py-2">
                  KPI PAR CATÉGORIES
                </h2>
              </div>
              <p className="text-[#EAEAEA]/60 mt-2 text-sm uppercase tracking-widest">
                {getPeriodLabel()}
              </p>
            </div>

            {/* Grille des 4 cartes KPI + Dépôts */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              
              {/* CARTE 1 - JEUX */}
              <div className="relative group">
                {/* Barre colorée en haut */}
                <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-[#10B981] to-[#059669] rounded-t-2xl"></div>
                
                <div className="relative bg-gradient-to-br from-[#2A3B5C] to-[#1C2541] rounded-2xl p-6 pt-8 border border-[#10B981]/40 hover:border-[#10B981] transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-[#10B981]/30">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-gradient-to-br from-[#10B981] to-[#059669] rounded-lg">
                      <Gamepad2 className="w-5 h-5 text-white" strokeWidth={2.5} />
                    </div>
                    <h3 className="text-xl font-black text-[#10B981]">JEUX</h3>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-[#EAEAEA]/60 text-sm">CA</span>
                      <span className="text-[#10B981] font-bold">{(displayStats.caParRubrique?.['JEUX'] || 0).toLocaleString()} FCFA</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[#EAEAEA]/60 text-sm">Dépenses</span>
                      <span className="text-red-400 font-bold">0 FCFA</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[#EAEAEA]/60 text-sm">Bénéfice</span>
                      <span className="text-[#10B981] font-bold">{(displayStats.caParRubrique?.['JEUX'] || 0).toLocaleString()} FCFA</span>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-[#10B981]/20">
                      <span className="text-[#EAEAEA]/60 text-sm">Marge</span>
                      <div className="flex items-center gap-1">
                        <span className="text-[#10B981] font-black">100.0%</span>
                        <ArrowUpRight className="w-4 h-4 text-[#10B981]" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* CARTE 2 - BOISSON */}
              <div className="relative group">
                <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-[#3B82F6] to-[#2563EB] rounded-t-2xl"></div>
                
                <div className="relative bg-gradient-to-br from-[#2A3B5C] to-[#1C2541] rounded-2xl p-6 pt-8 border border-[#3B82F6]/40 hover:border-[#3B82F6] transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-[#3B82F6]/30">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-gradient-to-br from-[#3B82F6] to-[#2563EB] rounded-lg">
                      <Wine className="w-5 h-5 text-white" strokeWidth={2.5} />
                    </div>
                    <h3 className="text-xl font-black text-[#3B82F6]">BOISSON</h3>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-[#EAEAEA]/60 text-sm">CA</span>
                      <span className="text-[#3B82F6] font-bold">{(displayStats.caParRubrique?.['BOISSON'] || 0).toLocaleString()} FCFA</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[#EAEAEA]/60 text-sm">Dépenses</span>
                      <span className="text-red-400 font-bold">0 FCFA</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[#EAEAEA]/60 text-sm">Bénéfice</span>
                      <span className="text-[#3B82F6] font-bold">{(displayStats.caParRubrique?.['BOISSON'] || 0).toLocaleString()} FCFA</span>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-[#3B82F6]/20">
                      <span className="text-[#EAEAEA]/60 text-sm">Marge</span>
                      <div className="flex items-center gap-1">
                        <span className="text-[#3B82F6] font-black">100.0%</span>
                        <ArrowUpRight className="w-4 h-4 text-[#3B82F6]" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* CARTE 3 - NESPRESSO */}
              <div className="relative group">
                <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-[#F59E0B] to-[#D97706] rounded-t-2xl"></div>
                
                <div className="relative bg-gradient-to-br from-[#2A3B5C] to-[#1C2541] rounded-2xl p-6 pt-8 border border-[#F59E0B]/40 hover:border-[#F59E0B] transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-[#F59E0B]/30">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-gradient-to-br from-[#F59E0B] to-[#D97706] rounded-lg">
                      <Coffee className="w-5 h-5 text-white" strokeWidth={2.5} />
                    </div>
                    <h3 className="text-xl font-black text-[#F59E0B]">NESPRESSO</h3>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-[#EAEAEA]/60 text-sm">CA</span>
                      <span className="text-[#F59E0B] font-bold">{(displayStats.caParRubrique?.['NESPRESSO'] || displayStats.caParRubrique?.['CAFÉ'] || displayStats.caParRubrique?.['CAFE'] || 0).toLocaleString()} FCFA</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[#EAEAEA]/60 text-sm">Dépenses</span>
                      <span className="text-red-400 font-bold">0 FCFA</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[#EAEAEA]/60 text-sm">Bénéfice</span>
                      <span className="text-[#F59E0B] font-bold">{(displayStats.caParRubrique?.['NESPRESSO'] || displayStats.caParRubrique?.['CAFÉ'] || displayStats.caParRubrique?.['CAFE'] || 0).toLocaleString()} FCFA</span>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-[#F59E0B]/20">
                      <span className="text-[#EAEAEA]/60 text-sm">Marge</span>
                      <div className="flex items-center gap-1">
                        <span className="text-[#F59E0B] font-black">100.0%</span>
                        <ArrowUpRight className="w-4 h-4 text-[#F59E0B]" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* CARTE 4 - DÉPENSES (CHARGES) */}
              <div className="relative group">
                <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-[#EF4444] to-[#DC2626] rounded-t-2xl"></div>
                
                <div className="relative bg-gradient-to-br from-[#2A3B5C] to-[#1C2541] rounded-2xl p-6 pt-8 border border-[#EF4444]/40 hover:border-[#EF4444] transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-[#EF4444]/30">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-gradient-to-br from-[#EF4444] to-[#DC2626] rounded-lg">
                      <Receipt className="w-5 h-5 text-white" strokeWidth={2.5} />
                    </div>
                    <h3 className="text-xl font-black text-[#EF4444]">CHARGES</h3>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-[#EAEAEA]/60 text-sm">Nombre</span>
                      <span className="text-[#EF4444] font-bold">{displayStats.nbDepenses || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[#EAEAEA]/60 text-sm">Total</span>
                      <span className="text-[#EF4444] font-bold">{(displayStats.totalDepenses || 0).toLocaleString()} FCFA</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[#EAEAEA]/60 text-sm">Type</span>
                      <span className="text-[#EAEAEA]/80 text-xs">CIE, LOYER...</span>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-[#EF4444]/20">
                      <span className="text-[#EAEAEA]/60 text-sm">Impact</span>
                      <div className="flex items-center gap-1">
                        <span className="text-[#EF4444] font-black">-{((displayStats.totalDepenses / displayStats.caGeneral) * 100 || 0).toFixed(1)}%</span>
                        <ArrowDownRight className="w-4 h-4 text-[#EF4444]" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>

            {/* CARTE DÉPÔTS BANCAIRES - Pleine largeur */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-[#8B5CF6]/10 to-[#7C3AED]/10 rounded-2xl blur-xl"></div>
              
              <div className="relative bg-gradient-to-br from-[#2A3B5C] to-[#1C2541] rounded-2xl p-6 border-2 border-[#8B5CF6]/40 hover:border-[#8B5CF6] transition-all duration-300">
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                  <div className="flex items-center gap-4">
                    <div className="p-4 bg-gradient-to-br from-[#8B5CF6] to-[#7C3AED] rounded-xl">
                      <Building2 className="w-8 h-8 text-white" strokeWidth={2.5} />
                    </div>
                    <div>
                      <h3 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#8B5CF6] to-[#7C3AED]">
                        Dépôts Bancaires
                      </h3>
                      <p className="text-[#EAEAEA]/60 text-sm mt-1">
                        {depotsBancaires.length} dépôt{depotsBancaires.length > 1 ? 's' : ''} effectué{depotsBancaires.length > 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-8">
                    <div className="text-center">
                      <p className="text-[#EAEAEA]/60 text-sm mb-1">Montant Total</p>
                      <p className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#8B5CF6] to-[#7C3AED]">
                        {(totalDepots || 0).toLocaleString()}
                      </p>
                      <p className="text-[#8B5CF6] text-sm font-bold">FCFA</p>
                    </div>

                    {depotsBancaires.length > 0 && (
                      <>
                        <div className="h-16 w-px bg-[#8B5CF6]/30"></div>

                        <div>
                          <p className="text-[#EAEAEA]/60 text-sm mb-2">Types de dépôt</p>
                          <div className="flex flex-wrap gap-2">
                            {(() => {
                              // Grouper les dépôts par banque et calculer le montant total par banque
                              const banqueStats = depotsBancaires.reduce((acc, depot) => {
                                const banque = depot.banque || 'Autre';
                                if (!acc[banque]) {
                                  acc[banque] = { count: 0, montant: 0 };
                                }
                                acc[banque].count += 1;
                                acc[banque].montant += depot.montant || 0;
                                return acc;
                              }, {});

                              // Couleurs pour différentes banques
                              const banqueColors = {
                                'WAVE': { bg: 'bg-[#8B5CF6]/20', border: 'border-[#8B5CF6]/40', text: 'text-[#8B5CF6]' },
                                'ORANGE MONEY': { bg: 'bg-[#F59E0B]/20', border: 'border-[#F59E0B]/40', text: 'text-[#F59E0B]' },
                                'BICICI': { bg: 'bg-blue-500/20', border: 'border-blue-500/40', text: 'text-blue-400' },
                                'SGCI': { bg: 'bg-red-500/20', border: 'border-red-500/40', text: 'text-red-400' },
                                'Ecobank': { bg: 'bg-green-500/20', border: 'border-green-500/40', text: 'text-green-400' }
                              };

                              return Object.entries(banqueStats).map(([banque, stats]) => {
                                const colors = banqueColors[banque] || { 
                                  bg: 'bg-gray-500/20', 
                                  border: 'border-gray-500/40', 
                                  text: 'text-gray-400' 
                                };
                                
                                return (
                                  <div 
                                    key={banque} 
                                    className={`px-3 py-2 ${colors.bg} border ${colors.border} rounded-lg ${colors.text} text-xs font-semibold flex flex-col items-center`}
                                    title={`${stats.montant.toLocaleString()} FCFA`}
                                  >
                                    <span>{banque}</span>
                                    <span className="text-[10px] opacity-80">{stats.count} × {(stats.montant / 1000).toFixed(0)}K</span>
                                  </div>
                                );
                              });
                            })()}
                          </div>
                        </div>
                      </>
                    )}

                    {depotsBancaires.length === 0 && (
                      <div className="text-[#EAEAEA]/40 text-sm italic">
                        Aucun dépôt pour cette période
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* SECTION 4 - PERFORMANCE DES JEUX & BOISSONS - ULTRA PREMIUM */}
        <div className="relative mb-12">
          {/* Effet de lueur en arrière-plan */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#10B981]/10 via-[#8B5CF6]/10 to-[#F59E0B]/10 rounded-3xl blur-2xl"></div>
          
          <div className="relative bg-gradient-to-br from-[#1C2541]/90 to-[#2A3B5C]/90 backdrop-blur-xl rounded-3xl p-8 border-2 border-[#10B981]/30 shadow-2xl">
            {/* Titre de la section */}
            <div className="mb-8 text-center">
              <div className="inline-block relative">
                <div className="absolute inset-0 bg-gradient-to-r from-[#10B981] to-[#8B5CF6] blur-lg opacity-50"></div>
                <h2 className="relative text-3xl md:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] via-[#8B5CF6] to-[#F59E0B] px-6 py-2">
                  PERFORMANCE DES JEUX & BOISSONS
                </h2>
              </div>
              <p className="text-[#EAEAEA]/60 mt-2 text-sm uppercase tracking-widest">
                {getPeriodLabel()}
              </p>
            </div>

            {/* Layout : 2 tableaux côte à côte */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
              
              {/* GAUCHE - TABLEAU JEUX */}
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-[#10B981]/10 to-[#059669]/10 rounded-2xl blur-xl"></div>
                <div className="relative bg-gradient-to-br from-[#2A3B5C] to-[#1C2541] rounded-2xl p-6 border border-[#10B981]/40">
                  
                  {/* Titre du tableau */}
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-3 bg-gradient-to-br from-[#10B981] to-[#059669] rounded-xl">
                      <Gamepad2 className="w-6 h-6 text-white" strokeWidth={3} />
                    </div>
                    <h3 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] to-[#059669]">
                      Jeux KPI
                    </h3>
                  </div>

                  {/* Tableau des jeux */}
                  {statsJeux && statsJeux.statsParConsole && statsJeux.statsParConsole.length > 0 ? (
                    <>
                      {/* TABLEAU PREMIUM - Responsive pour tous écrans */}
                      <div className="overflow-x-auto">
                        <table className="w-full min-w-full">
                          {/* En-têtes */}
                          <thead>
                            <tr className="bg-gradient-to-r from-[#10B981] to-[#059669] text-white">
                              <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm font-bold uppercase tracking-wider rounded-tl-lg">
                                Métrique
                              </th>
                              {statsJeux.statsParConsole.map((console, index) => (
                                <th key={console.console} className={`px-2 sm:px-4 py-2 sm:py-3 text-center text-xs sm:text-sm font-bold uppercase tracking-wider ${
                                  index === statsJeux.statsParConsole.length - 1 ? 'rounded-tr-lg' : ''
                                }`}>
                                  {console.console}
                                </th>
                              ))}
                            </tr>
                          </thead>

                          {/* Corps du tableau */}
                          <tbody className="divide-y divide-[#10B981]/20">
                            {/* Ligne CA Total */}
                            <tr className="hover:bg-[#10B981]/10 transition-colors">
                              <td className="px-2 sm:px-4 py-2 sm:py-4 text-[#EAEAEA] font-semibold text-xs sm:text-base">
                                CA Total
                              </td>
                              {statsJeux.statsParConsole.map(console => (
                                <td key={console.console} className="px-2 sm:px-4 py-2 sm:py-4 text-center">
                                  <span className="text-[#10B981] font-bold text-sm sm:text-lg">
                                    {console.ca.toLocaleString()}
                                  </span>
                                  <p className="text-[#EAEAEA]/40 text-[10px] sm:text-xs">FCFA</p>
                                </td>
                              ))}
                            </tr>

                            {/* Ligne Total Session */}
                            <tr className="hover:bg-[#059669]/10 transition-colors">
                              <td className="px-2 sm:px-4 py-2 sm:py-4 text-[#EAEAEA] font-semibold text-xs sm:text-base">
                                Total Session
                              </td>
                              {statsJeux.statsParConsole.map(console => (
                                <td key={console.console} className="px-2 sm:px-4 py-2 sm:py-4 text-center">
                                  <span className="text-[#10B981] font-bold text-sm sm:text-lg">
                                    {console.nbSessions}
                                  </span>
                                </td>
                              ))}
                            </tr>

                            {/* Lignes des jeux par nombre de sessions */}
                            {(() => {
                              // Liste des 7 jeux standards UNIQUEMENT
                              const jeuxStandards = [
                                "FC 24",
                                "FC 25", 
                                "FC 26",
                                "Call of Duty",
                                "Tekken 8",
                                "Fortenite",
                                "Zombie"
                              ];

                              return jeuxStandards.map((nomJeu, index) => (
                                <tr key={nomJeu} className="hover:bg-[#10B981]/10 transition-colors">
                                  <td className="px-2 sm:px-4 py-2 sm:py-4 text-[#EAEAEA] font-semibold pl-4 sm:pl-8 text-xs sm:text-base">
                                    {nomJeu}
                                  </td>
                                  {statsJeux.statsParConsole.map(console => {
                                    const jeu = console.jeux.find(j => j.nom === nomJeu);
                                    return (
                                      <td key={console.console} className="px-2 sm:px-4 py-2 sm:py-4 text-center">
                                        {jeu ? (
                                          <span className="text-[#10B981] font-bold text-sm sm:text-lg">
                                            {jeu.nbSessions}
                                          </span>
                                        ) : (
                                          <span className="text-[#EAEAEA]/40">-</span>
                                        )}
                                      </td>
                                    );
                                  })}
                                </tr>
                              ));
                            })()}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-8 text-[#EAEAEA]/40">
                      Aucune donnée de jeux pour cette période
                    </div>
                  )}

                </div>
              </div>

              {/* DROITE - TABLEAU BOISSONS */}
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-[#3B82F6]/10 to-[#2563EB]/10 rounded-2xl blur-xl"></div>
                <div className="relative bg-gradient-to-br from-[#2A3B5C] to-[#1C2541] rounded-2xl p-6 border border-[#3B82F6]/40">
                  
                  {/* Titre du tableau */}
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-3 bg-gradient-to-br from-[#3B82F6] to-[#2563EB] rounded-xl">
                      <Wine className="w-6 h-6 text-white" strokeWidth={3} />
                    </div>
                    <h3 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#3B82F6] to-[#2563EB]">
                      Boisson KPI
                    </h3>
                  </div>

                  {/* Tableau des boissons - ULTRA PREMIUM - Responsive */}
                  {statsBoissons && statsBoissons.statsParBoisson && statsBoissons.statsParBoisson.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-full">
                        {/* En-têtes */}
                        <thead>
                          <tr className="bg-gradient-to-r from-[#3B82F6] to-[#2563EB] text-white">
                            <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm font-bold uppercase tracking-wider rounded-tl-lg">
                              Boisson
                            </th>
                            <th className="px-2 sm:px-4 py-2 sm:py-3 text-center text-xs sm:text-sm font-bold uppercase tracking-wider">
                              Nbre Vendu
                            </th>
                            <th className="px-2 sm:px-4 py-2 sm:py-3 text-center text-xs sm:text-sm font-bold uppercase tracking-wider">
                              CA
                            </th>
                            <th className="px-2 sm:px-4 py-2 sm:py-3 text-center text-xs sm:text-sm font-bold uppercase tracking-wider">
                              Stock Init.
                            </th>
                            <th className="px-2 sm:px-4 py-2 sm:py-3 text-center text-xs sm:text-sm font-bold uppercase tracking-wider rounded-tr-lg">
                              Stock Rest.
                            </th>
                          </tr>
                        </thead>

                        {/* Corps du tableau */}
                        <tbody className="divide-y divide-[#3B82F6]/20">
                          {statsBoissons.statsParBoisson.map((boisson, index) => (
                            <tr key={boisson.nom} className={`transition-colors ${
                              boisson.nom === "🍺 TOTAL" ? "bg-[#FFD700]/10 hover:bg-[#FFD700]/20" : "hover:bg-[#3B82F6]/10"
                            }`}>
                              <td className={`px-2 sm:px-4 py-2 sm:py-4 text-xs sm:text-base font-semibold ${
                                boisson.nom === "🍺 TOTAL" ? "text-[#FFD700]" : "text-[#EAEAEA]"
                              }`}>
                                {boisson.nom}
                              </td>
                              <td className="px-2 sm:px-4 py-2 sm:py-4 text-center">
                                <span className="text-[#3B82F6] font-bold text-sm sm:text-lg">
                                  {boisson.nbVendu}
                                </span>
                              </td>
                              <td className="px-2 sm:px-4 py-2 sm:py-4 text-center">
                                <span className="text-[#10B981] font-bold text-sm sm:text-lg">
                                  {boisson.ca.toLocaleString()}
                                </span>
                                <p className="text-[#EAEAEA]/40 text-[10px] sm:text-xs">FCFA</p>
                              </td>
                              <td className="px-2 sm:px-4 py-2 sm:py-4 text-center">
                                <span className="text-[#EAEAEA] font-bold text-sm sm:text-lg">
                                  {boisson.stockInitial || 0}
                                </span>
                              </td>
                              <td className="px-2 sm:px-4 py-2 sm:py-4 text-center">
                                <div className="flex flex-col items-center gap-1">
                                  <span className={`font-bold text-sm sm:text-lg ${
                                    (boisson.pourcentageRestant || 0) < 20 ? 'text-red-400' : 
                                    (boisson.pourcentageRestant || 0) < 50 ? 'text-yellow-400' : 
                                    'text-[#10B981]'
                                  }`}>
                                    {boisson.stockRestant || 0}
                                  </span>
                                  {boisson.nom !== "🍺 TOTAL" && (
                                    <span className={`text-[10px] sm:text-xs ${
                                      (boisson.pourcentageRestant || 0) < 20 ? 'text-red-400' : 
                                      (boisson.pourcentageRestant || 0) < 50 ? 'text-yellow-400' : 
                                      'text-[#10B981]'
                                    }`}>
                                      ({boisson.pourcentageRestant || 0}%)
                                    </span>
                                  )}
                                  {/* Barre de progression du stock */}
                                  {boisson.nom !== "🍺 TOTAL" && boisson.stockInitial > 0 && (
                                    <div className="w-12 sm:w-20 bg-gray-700 rounded-full h-1 sm:h-1.5 mt-1">
                                      <div 
                                        className={`h-1 sm:h-1.5 rounded-full transition-all ${
                                          (boisson.pourcentageRestant || 0) < 20 ? 'bg-red-500' : 
                                          (boisson.pourcentageRestant || 0) < 50 ? 'bg-yellow-500' : 
                                          'bg-green-500'
                                        }`}
                                        style={{ width: `${boisson.pourcentageRestant || 0}%` }}
                                      ></div>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-[#EAEAEA]/40">
                      Aucune donnée de boissons pour cette période
                    </div>
                  )}

                </div>
              </div>

            </div>
          </div>
        </div>

        {/* SECTION 5 - CLASSEMENT DES CLIENTS - ULTRA PREMIUM */}
        <div className="relative mb-12">
          {/* Effet de lueur en arrière-plan */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#F59E0B]/10 via-[#EF4444]/10 to-[#F59E0B]/10 rounded-3xl blur-2xl"></div>
          
          <div className="relative bg-gradient-to-br from-[#1C2541]/90 to-[#2A3B5C]/90 backdrop-blur-xl rounded-3xl p-8 border-2 border-[#F59E0B]/30 shadow-2xl">
            {/* Titre de la section */}
            <div className="mb-8 text-center">
              <div className="inline-block relative">
                <div className="absolute inset-0 bg-gradient-to-r from-[#F59E0B] to-[#EF4444] blur-lg opacity-50"></div>
                <h2 className="relative text-3xl md:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#F59E0B] via-[#EF4444] to-[#F59E0B] px-6 py-2">
                  CLASSEMENT DES CLIENTS
                </h2>
              </div>
              <p className="text-[#EAEAEA]/60 mt-2 text-sm uppercase tracking-widest">
                Programme de Fidélité - {getPeriodLabel()}
              </p>
            </div>

            {/* Layout : TOP 3 à gauche + Tableau à droite */}
            {statsClients && (
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                
                {/* GAUCHE - TOP 3 CLIENTS (PODIUM) */}
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#FFD700]/10 to-[#C6A15B]/10 rounded-2xl blur-xl"></div>
                  <div className="relative bg-gradient-to-br from-[#2A3B5C] to-[#1C2541] rounded-2xl p-6 border border-[#FFD700]/40">
                    
                    {/* Titre */}
                    <div className="flex items-center gap-3 mb-6">
                      <div className="p-3 bg-gradient-to-br from-[#FFD700] to-[#C6A15B] rounded-xl">
                        <Trophy className="w-6 h-6 text-[#0B132B]" strokeWidth={3} />
                      </div>
                      <h3 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#FFD700] to-[#C6A15B]">
                        TOP 3 Clients VIP
                      </h3>
                    </div>

                    {/* Podium */}
                    <div className="space-y-4">
                      {statsClients.top3 && statsClients.top3.map((client, index) => {
                        const colors = [
                          { bg: 'from-yellow-500 to-amber-500', text: 'text-yellow-400', border: 'border-yellow-500/40', shadow: 'shadow-yellow-500/30', medal: '🥇' },
                          { bg: 'from-gray-400 to-gray-500', text: 'text-gray-400', border: 'border-gray-500/40', shadow: 'shadow-gray-500/30', medal: '🥈' },
                          { bg: 'from-orange-600 to-orange-700', text: 'text-orange-400', border: 'border-orange-600/40', shadow: 'shadow-orange-600/30', medal: '🥉' }
                        ];
                        const style = colors[index];

                        return (
                          <div key={client.nom} className={`group relative hover:scale-105 transition-all duration-300`}>
                            <div className={`absolute inset-0 bg-gradient-to-r ${style.bg} opacity-20 rounded-xl blur-xl group-hover:blur-2xl transition-all`}></div>
                            <div className={`relative bg-gradient-to-br from-[#2A3B5C] to-[#1C2541] rounded-xl p-4 sm:p-5 border ${style.border} hover:border-opacity-100 transition-all hover:shadow-2xl ${style.shadow}`}>
                              <div className="flex items-center gap-3 sm:gap-4">
                                {/* Médaille */}
                                <div className={`flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gradient-to-br ${style.bg} flex items-center justify-center text-xl sm:text-2xl font-black shadow-lg`}>
                                  {index + 1}
                                </div>
                                
                                {/* Info Client */}
                                <div className="flex-1 min-w-0">
                                  <h4 className={`text-base sm:text-lg font-bold ${style.text} break-words`}>
                                    {client.nom}
                                  </h4>
                                  <p className="text-[#EAEAEA]/60 text-xs sm:text-sm">
                                    {client.nbSessions} sessions
                                  </p>
                                  <p className="text-[#EAEAEA]/40 text-xs line-clamp-1">
                                    {client.jeux.slice(0, 2).join(', ')}
                                  </p>
                                </div>

                                {/* CA */}
                                <div className="text-right flex-shrink-0">
                                  <p className={`text-lg sm:text-2xl font-black ${style.text}`}>
                                    {client.ca.toLocaleString()}
                                  </p>
                                  <p className="text-[#EAEAEA]/40 text-xs">FCFA</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* DROITE - TABLEAU CLIENTS RÉGULIERS (>2 sessions) */}
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#EF4444]/10 to-[#F59E0B]/10 rounded-2xl blur-xl"></div>
                  <div className="relative bg-gradient-to-br from-[#2A3B5C] to-[#1C2541] rounded-2xl p-6 border border-[#EF4444]/40">
                    
                    {/* Titre */}
                    <div className="flex items-center gap-3 mb-6">
                      <div className="p-3 bg-gradient-to-br from-[#EF4444] to-[#F59E0B] rounded-xl">
                        <Users className="w-6 h-6 text-white" strokeWidth={3} />
                      </div>
                      <h3 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#EF4444] to-[#F59E0B]">
                        Clients Réguliers
                      </h3>
                    </div>

                    {/* Tableau */}
                    {statsClients.clientsReguliers && statsClients.clientsReguliers.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          {/* En-têtes */}
                          <thead>
                            <tr className="bg-gradient-to-r from-[#EF4444] to-[#F59E0B] text-white">
                              <th className="px-4 py-3 text-left text-sm font-bold uppercase tracking-wider rounded-tl-lg">
                                Client
                              </th>
                              <th className="px-4 py-3 text-center text-sm font-bold uppercase tracking-wider">
                                Sessions
                              </th>
                              <th className="px-4 py-3 text-center text-sm font-bold uppercase tracking-wider">
                                CA
                              </th>
                              <th className="px-4 py-3 text-center text-sm font-bold uppercase tracking-wider">
                                Consoles
                              </th>
                              <th className="px-4 py-3 text-center text-sm font-bold uppercase tracking-wider rounded-tr-lg">
                                Jeux
                              </th>
                            </tr>
                          </thead>

                          {/* Corps du tableau */}
                          <tbody className="divide-y divide-[#EF4444]/20">
                            {statsClients.clientsReguliers.map((client, index) => (
                              <tr key={client.nom} className="hover:bg-[#EF4444]/10 transition-colors">
                                <td className="px-4 py-4 text-[#EAEAEA] font-semibold">
                                  {client.nom}
                                </td>
                                <td className="px-4 py-4 text-center">
                                  <span className="text-[#F59E0B] font-bold text-lg">
                                    {client.nbSessions}
                                  </span>
                                </td>
                                <td className="px-4 py-4 text-center">
                                  <div>
                                    <span className="text-[#10B981] font-bold text-lg">
                                      {client.ca.toLocaleString()}
                                    </span>
                                    <p className="text-[#EAEAEA]/40 text-xs">FCFA</p>
                                  </div>
                                </td>
                                <td className="px-4 py-4 text-center">
                                  <p className="text-[#3B82F6] text-sm font-semibold">
                                    {client.consoles.join(', ')}
                                  </p>
                                </td>
                                <td className="px-4 py-4 text-center">
                                  <p className="text-[#EAEAEA]/80 text-sm">
                                    {client.jeux.slice(0, 2).join(', ')}
                                  </p>
                                  {client.jeux.length > 2 && (
                                    <p className="text-[#EAEAEA]/40 text-xs">
                                      +{client.jeux.length - 2} autres
                                    </p>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-[#EAEAEA]/40">
                        Aucun client régulier pour cette période
                      </div>
                    )}

                  </div>
                </div>

              </div>
            )}

          </div>

          {/* SECTION 6 - ÉVOLUTION DU CA MENSUEL - ULTRA PREMIUM */}
          <div className="relative">
            <div className="absolute -inset-1 bg-gradient-to-r from-[#8B5CF6] via-[#06B6D4] to-[#8B5CF6] rounded-lg blur-xl opacity-30 animate-pulse"></div>
            <Card className="relative glass-effect border-2 border-[#8B5CF6]/40 hover:border-[#06B6D4]/60 transition-all duration-500 hover:shadow-2xl hover:shadow-[#06B6D4]/30">
              <CardHeader className="border-b border-[#8B5CF6]/20">
                <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <div className="absolute inset-0 bg-gradient-to-br from-[#8B5CF6] to-[#06B6D4] rounded-xl blur-md opacity-60"></div>
                      <div className="relative p-3 rounded-xl bg-gradient-to-br from-[#8B5CF6] to-[#06B6D4] shadow-lg">
                        <TrendingUp className="w-7 h-7 text-white" />
                      </div>
                    </div>
                    <div>
                      <CardTitle className="text-3xl lg:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#8B5CF6] via-[#06B6D4] to-[#8B5CF6] animate-gradient">
                        📈 Évolution du CA Annuel
                      </CardTitle>
                      <p className="text-[#8B5CF6]/70 text-sm mt-1">Performance Mensuelle • Démarrage Sept 2025</p>
                    </div>
                  </div>
                  
                  {/* Sélecteur d'année */}
                  <div className="flex items-center gap-3 bg-gradient-to-r from-[#8B5CF6]/10 to-[#06B6D4]/10 rounded-xl p-2 border border-[#8B5CF6]/30">
                    <span className="text-[#EAEAEA] text-sm font-semibold px-2">Année :</span>
                    {availableYears.map(year => (
                      <button
                        key={year}
                        onClick={() => setSelectedYear(year)}
                        className={`px-4 py-2 rounded-lg font-bold transition-all duration-300 ${
                          selectedYear === year
                            ? 'bg-gradient-to-r from-[#8B5CF6] to-[#06B6D4] text-white shadow-lg shadow-[#8B5CF6]/50 scale-105'
                            : 'text-[#EAEAEA]/60 hover:text-[#8B5CF6] hover:bg-[#8B5CF6]/10'
                        }`}
                      >
                        {year}
                      </button>
                    ))}
                  </div>
                </div>
              </CardHeader>

              <CardContent className="pt-6">
                {caMensuel && caMensuel.length > 0 ? (
                  <div className="space-y-6">
                    
                    {/* Légende Ultra Premium - Mise à jour */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="flex items-center justify-center gap-3 bg-gradient-to-r from-[#06B6D4]/10 to-[#0891B2]/10 rounded-xl p-3 border border-[#06B6D4]/30">
                        <div className="w-10 h-1 bg-gradient-to-r from-[#06B6D4] to-[#0891B2] rounded-full shadow-lg shadow-[#06B6D4]/50"></div>
                        <span className="text-[#EAEAEA] text-sm font-semibold">CA</span>
                      </div>
                      <div className="flex items-center justify-center gap-3 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-xl p-3 border border-purple-500/30">
                        <div className="w-10 h-1 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"></div>
                        <span className="text-[#EAEAEA] text-sm font-semibold">Dépôts</span>
                      </div>
                      <div className="flex items-center justify-center gap-3 bg-gradient-to-r from-orange-500/10 to-red-500/10 rounded-xl p-3 border border-orange-500/30">
                        <div className="w-10 h-1 bg-gradient-to-r from-orange-500 to-red-500 rounded-full"></div>
                        <span className="text-[#EAEAEA] text-sm font-semibold">Charges</span>
                      </div>
                      <div className="flex items-center justify-center gap-2 bg-gradient-to-r from-[#8B5CF6]/10 to-[#7C3AED]/10 rounded-xl p-3 border border-[#8B5CF6]/30">
                        <span className="text-[#EAEAEA]/80 text-xs">🎯 Obj: 700K</span>
                      </div>
                    </div>

                    {/* Graphique Ultra Premium - Composite avec courbes seulement (sans bénéfice) */}
                    <div className="relative bg-gradient-to-br from-[#1C2541] via-[#0B132B] to-[#1C2541] rounded-2xl p-3 sm:p-6 border-2 border-[#8B5CF6]/20 shadow-2xl">
                      <div className="absolute -inset-1 bg-gradient-to-r from-[#8B5CF6] via-[#06B6D4] to-[#8B5CF6] rounded-2xl blur opacity-10"></div>
                      <div className="relative">
                        <ResponsiveContainer width="100%" height={350} minWidth={0}>
                          <ComposedChart data={caMensuel} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#8B5CF6" opacity={0.15} />
                            <XAxis 
                              dataKey="mois" 
                              stroke="#8B5CF6"
                              tick={{ fill: '#8B5CF6', fontSize: 13, fontWeight: 'bold' }}
                              height={40}
                            />
                            <YAxis 
                              stroke="#06B6D4"
                              tick={{ fill: '#06B6D4', fontSize: 13, fontWeight: 'bold' }}
                              domain={[0, 800000]}
                              tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
                            />
                            <Tooltip 
                              contentStyle={{
                                backgroundColor: '#0B132B',
                                border: '2px solid #8B5CF6',
                                borderRadius: '16px',
                                color: '#EAEAEA',
                                boxShadow: '0 0 30px rgba(139, 92, 246, 0.5)',
                                padding: '12px'
                              }}
                              formatter={(value, name) => {
                                const labels = {
                                  ca: 'CA Réalisé',
                                  depots: 'Dépôts',
                                  charges: 'Charges'
                                };
                                return [`${value.toLocaleString()} FCFA`, labels[name] || name];
                              }}
                              labelStyle={{ color: '#06B6D4', fontWeight: 'bold', fontSize: '14px' }}
                            />
                            
                            {/* Ligne de référence - Objectif 700K */}
                            <Line 
                              type="monotone" 
                              dataKey={() => 700000}
                              stroke="#EF4444" 
                              strokeWidth={2}
                              strokeDasharray="8 8"
                              dot={false}
                              name="Objectif"
                            />
                            
                            {/* Courbe du CA - Cyan */}
                            <Line 
                              type="monotone" 
                              dataKey="ca" 
                              stroke="#06B6D4" 
                              strokeWidth={4}
                              dot={{ fill: '#06B6D4', r: 6 }}
                              activeDot={{ r: 8 }}
                              name="ca"
                            />
                            
                            {/* Courbe des Dépôts - Purple */}
                            <Line 
                              type="monotone" 
                              dataKey="depots" 
                              stroke="#A855F7" 
                              strokeWidth={3}
                              dot={{ fill: '#A855F7', r: 5 }}
                              activeDot={{ r: 7 }}
                              name="depots"
                            />
                            
                            {/* Courbe des Charges - Orange/Red */}
                            <Line 
                              type="monotone" 
                              dataKey="charges" 
                              stroke="#F97316" 
                              strokeWidth={3}
                              dot={{ fill: '#F97316', r: 5 }}
                              activeDot={{ r: 7 }}
                              name="charges"
                            />
                          </ComposedChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    {/* Stats résumé ULTRA PREMIUM LUXE */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                      {/* CA Total - PREMIUM VERT */}
                      <div className="relative group cursor-pointer transform hover:scale-105 transition-all duration-500">
                        <div className="absolute -inset-2 bg-gradient-to-r from-[#10B981] via-[#059669] to-[#047857] rounded-2xl blur-xl opacity-40 group-hover:opacity-75 animate-pulse transition duration-500"></div>
                        <div className="relative bg-gradient-to-br from-[#0B132B] via-[#1C2541] to-[#0B132B] rounded-2xl p-6 border-2 border-[#10B981] shadow-2xl shadow-[#10B981]/50 overflow-hidden">
                          {/* Effet de brillance animé */}
                          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#10B981] to-transparent animate-shimmer"></div>
                          
                          <div className="relative z-10">
                            <div className="flex items-center justify-between mb-4">
                              <p className="text-[#10B981] text-xs font-black uppercase tracking-[0.15em] flex items-center gap-2">
                                <span className="inline-block w-2 h-2 bg-[#10B981] rounded-full animate-ping"></span>
                                CA TOTAL {selectedYear}
                              </p>
                              <div className="text-3xl">💰</div>
                            </div>
                            
                            <div className="space-y-1">
                              <p className="text-6xl font-black text-transparent bg-clip-text bg-gradient-to-br from-[#10B981] via-[#34D399] to-[#6EE7B7] drop-shadow-2xl animate-glow">
                                {caMensuel.reduce((sum, item) => sum + item.ca, 0).toLocaleString()}
                              </p>
                              <p className="text-[#10B981] text-xl font-black tracking-wider">FCFA</p>
                            </div>
                          </div>
                          
                          {/* Effet de fond animé */}
                          <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-[#10B981]/10 rounded-full blur-3xl"></div>
                        </div>
                      </div>

                      {/* Meilleur Mois - ULTRA PREMIUM OR */}
                      <div className="relative group cursor-pointer transform hover:scale-110 transition-all duration-500">
                        <div className="absolute -inset-3 bg-gradient-to-r from-[#FFD700] via-[#FFA500] to-[#FF8C00] rounded-2xl blur-2xl opacity-60 group-hover:opacity-100 animate-pulse transition duration-500"></div>
                        <div className="relative bg-gradient-to-br from-[#1C2541] via-[#2A3B5C] to-[#1C2541] rounded-2xl p-6 border-4 border-[#FFD700] shadow-2xl shadow-[#FFD700]/70 overflow-hidden">
                          {/* Effet de brillance dorée */}
                          <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-transparent via-[#FFD700] to-transparent animate-shimmer"></div>
                          <div className="absolute bottom-0 right-0 w-full h-2 bg-gradient-to-l from-transparent via-[#FFD700] to-transparent animate-shimmer" style={{animationDelay: '1s'}}></div>
                          
                          <div className="relative z-10">
                            <div className="flex items-center justify-between mb-3">
                              <p className="text-[#FFD700] text-xs font-black uppercase tracking-[0.2em] flex items-center gap-2">
                                <span className="text-2xl">👑</span>
                                MEILLEUR MOIS
                              </p>
                              <div className="text-4xl animate-bounce">🏆</div>
                            </div>
                            
                            <div className="space-y-2">
                              <div className="flex items-center justify-center gap-3 mb-2">
                                <div className="px-4 py-2 bg-gradient-to-r from-[#FFD700] to-[#FFA500] rounded-lg shadow-lg shadow-[#FFD700]/50">
                                  <p className="text-3xl font-black text-[#0B132B]">
                                    {caMensuel.reduce((max, item) => item.ca > max.ca ? item : max, caMensuel[0])?.mois}
                                  </p>
                                </div>
                              </div>
                              
                              <p className="text-7xl font-black text-center text-transparent bg-clip-text bg-gradient-to-br from-[#FFD700] via-[#FFA500] to-[#FF8C00] drop-shadow-2xl animate-glow">
                                {caMensuel.reduce((max, item) => item.ca > max.ca ? item : max, caMensuel[0])?.ca.toLocaleString()}
                              </p>
                              <p className="text-[#FFD700] text-2xl font-black text-center tracking-widest">FCFA</p>
                            </div>
                          </div>
                          
                          {/* Étoiles animées */}
                          <div className="absolute top-4 left-4 text-[#FFD700] text-xl animate-pulse">✨</div>
                          <div className="absolute top-8 right-6 text-[#FFD700] text-2xl animate-pulse" style={{animationDelay: '0.5s'}}>⭐</div>
                          <div className="absolute bottom-6 left-8 text-[#FFD700] text-lg animate-pulse" style={{animationDelay: '1s'}}>💫</div>
                          
                          {/* Effet de fond animé */}
                          <div className="absolute -bottom-20 -right-20 w-60 h-60 bg-[#FFD700]/10 rounded-full blur-3xl animate-pulse"></div>
                        </div>
                      </div>

                      {/* Moyenne CA Mois en Cours - PREMIUM BLEU */}
                      <div className="relative group cursor-pointer transform hover:scale-105 transition-all duration-500">
                        <div className="absolute -inset-2 bg-gradient-to-r from-[#3B82F6] via-[#2563EB] to-[#1D4ED8] rounded-2xl blur-xl opacity-40 group-hover:opacity-75 animate-pulse transition duration-500"></div>
                        <div className="relative bg-gradient-to-br from-[#0B132B] via-[#1C2541] to-[#0B132B] rounded-2xl p-6 border-2 border-[#3B82F6] shadow-2xl shadow-[#3B82F6]/50 overflow-hidden">
                          {/* Effet de brillance animé */}
                          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#3B82F6] to-transparent animate-shimmer"></div>
                          
                          <div className="relative z-10">
                            <div className="flex items-center justify-between mb-4">
                              <p className="text-[#3B82F6] text-xs font-black uppercase tracking-[0.1em] flex items-center gap-2">
                                <span className="inline-block w-2 h-2 bg-[#3B82F6] rounded-full animate-ping"></span>
                                MOYENNE CA DU MOIS EN COURS
                              </p>
                              <div className="text-3xl">📊</div>
                            </div>
                            
                            <div className="space-y-1">
                              {loading ? (
                                <div className="flex items-center justify-center h-24">
                                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#3B82F6]"></div>
                                </div>
                              ) : (
                                <>
                                  <p className="text-6xl font-black text-transparent bg-clip-text bg-gradient-to-br from-[#3B82F6] via-[#60A5FA] to-[#93C5FD] drop-shadow-2xl animate-glow">
                                    {(() => {
                                      const now = new Date();
                                      let caTotal = 0;
                                      let joursEcoules = 0;
                                      
                                      // Source de données : TOUJOURS stats.caGeneral
                                      if (stats && stats.caGeneral !== undefined) {
                                        caTotal = stats.caGeneral;
                                      } else {
                                        caTotal = cashFlowData.ca || 0;
                                      }
                                      
                                      const currentMonth = now.getMonth() + 1;
                                      const currentYear = now.getFullYear();
                                      const heureActuelle = now.getHours();
                                      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                                      
                                      // CAS 1 : Aujourd'hui
                                      if (period === 'today') {
                                        joursEcoules = heureActuelle < 22 ? 0 : 1;
                                        joursEcoules = Math.max(joursEcoules, 1);
                                      }
                                      
                                      // CAS 2 : Semaine
                                      else if (period === 'week') {
                                        const dayOfWeek = now.getDay();
                                        joursEcoules = heureActuelle < 22 ? dayOfWeek : dayOfWeek + 1;
                                        if (dayOfWeek === 0) joursEcoules = heureActuelle < 22 ? 6 : 7;
                                        joursEcoules = Math.max(joursEcoules, 1);
                                      }
                                      
                                      // CAS 3 : Mois
                                      else if (period === 'month') {
                                        joursEcoules = heureActuelle < 22 ? now.getDate() - 1 : now.getDate();
                                        joursEcoules = Math.max(joursEcoules, 1);
                                      }
                                      
                                      // CAS 4 : Période - Plage de dates
                                      else if (period === 'custom' && startDate && endDate) {
                                        const start = new Date(startDate);
                                        const end = new Date(endDate);
                                        start.setHours(0, 0, 0, 0);
                                        end.setHours(0, 0, 0, 0);
                                        
                                        // Compter les jours jour par jour (méthode fiable)
                                        let dayCount = 0;
                                        const currentDate = new Date(start);
                                        while (currentDate <= end) {
                                          dayCount++;
                                          currentDate.setDate(currentDate.getDate() + 1);
                                        }
                                        joursEcoules = dayCount;
                                        
                                        // RÈGLE 22H : Si la date de fin est aujourd'hui ET l'heure < 22h
                                        const endDateOnly = new Date(end.getFullYear(), end.getMonth(), end.getDate());
                                        if (endDateOnly.getTime() === today.getTime() && heureActuelle < 22) {
                                          // Exclure aujourd'hui car la journée n'est pas "complète"
                                          joursEcoules = joursEcoules - 1;
                                        }
                                        
                                        joursEcoules = Math.max(joursEcoules, 1);
                                      }
                                      
                                      // CAS 5 : Période - Mois complet
                                      else if (period === 'customMonth' && selectedMonth) {
                                        const { month, year } = selectedMonth;
                                        
                                        // Si c'est le mois en cours
                                        if (month === currentMonth && year === currentYear) {
                                          joursEcoules = heureActuelle < 22 ? now.getDate() - 1 : now.getDate();
                                          joursEcoules = Math.max(joursEcoules, 1);
                                        } else {
                                          // Mois passé : tous les jours
                                          joursEcoules = new Date(year, month, 0).getDate();
                                        }
                                      }
                                      
                                      // CAS 6 : Défaut (mois en cours)
                                      else {
                                        joursEcoules = heureActuelle < 22 ? now.getDate() - 1 : now.getDate();
                                        joursEcoules = Math.max(joursEcoules, 1);
                                      }
                                      
                                      // Calculer la moyenne
                                      const moyenne = joursEcoules > 0 ? Math.round(caTotal / joursEcoules) : 0;
                                      
                                      return moyenne.toLocaleString();
                                    })()}
                                  </p>
                                  <p className="text-[#3B82F6] text-xl font-black tracking-wider">FCFA</p>
                                </>
                              )}
                            </div>
                          </div>
                          
                          {/* Effet de fond animé */}
                          <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-[#3B82F6]/10 rounded-full blur-3xl"></div>
                        </div>
                      </div>
                    </div>

                  </div>
                ) : (
                  <div className="text-center py-16 text-[#EAEAEA]/40">
                    <BarChart3 className="w-20 h-20 mx-auto mb-4 opacity-30 animate-pulse" />
                    <p className="text-lg">Aucune donnée pour {selectedYear}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

        </div>

      </div>
    </div>
  );
};

export default Dashboard;
